import React from "react";

/**
 * VideoEmbed — paste any YouTube / Vimeo / Facebook / Instagram / TikTok URL
 * and we auto-detect the platform and render the right embed iframe.
 *
 * Falls back to a generic <video> tag if the URL ends in .mp4/.webm/.mov.
 */

function youtubeId(url) {
  // Handles: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/shorts/ID,
  // youtube.com/embed/ID, m.youtube.com, etc.
  const m =
    url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

function vimeoId(url) {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function tiktokInfo(url) {
  // tiktok.com/@user/video/VIDEOID
  const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  return m ? { id: m[1] } : null;
}

function looksLikeFacebook(url) {
  return /(?:facebook\.com|fb\.watch)/.test(url);
}

function looksLikeInstagram(url) {
  return /instagram\.com\/(?:p|reel|tv)\//.test(url);
}

function looksLikeDirectVideo(url) {
  return /\.(?:mp4|webm|mov|m4v)(?:\?|$)/i.test(url);
}

export default function VideoEmbed({ url, title }) {
  if (!url) return null;

  const yt = youtubeId(url);
  if (yt) {
    return (
      <div className="relative w-full aspect-video bg-atc-black border-2 border-atc-black overflow-hidden" data-testid="video-embed-youtube">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${yt}`}
          title={title || "YouTube video"}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  const vim = vimeoId(url);
  if (vim) {
    return (
      <div className="relative w-full aspect-video bg-atc-black border-2 border-atc-black overflow-hidden" data-testid="video-embed-vimeo">
        <iframe
          src={`https://player.vimeo.com/video/${vim}`}
          title={title || "Vimeo video"}
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  if (looksLikeFacebook(url)) {
    const src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=false`;
    return (
      <div className="relative w-full aspect-video bg-atc-black border-2 border-atc-black overflow-hidden" data-testid="video-embed-facebook">
        <iframe
          src={src}
          title={title || "Facebook video"}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  if (looksLikeInstagram(url)) {
    // Strip query string for clean embed URL
    const clean = url.split("?")[0].replace(/\/$/, "");
    const src = `${clean}/embed`;
    return (
      <div className="bg-atc-black border-2 border-atc-black overflow-hidden" data-testid="video-embed-instagram">
        <iframe
          src={src}
          title={title || "Instagram post"}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          className="w-full"
          style={{ minHeight: "720px", maxHeight: "900px" }}
        />
      </div>
    );
  }

  const tt = tiktokInfo(url);
  if (tt) {
    return (
      <div className="bg-atc-black border-2 border-atc-black overflow-hidden flex justify-center" data-testid="video-embed-tiktok">
        <iframe
          src={`https://www.tiktok.com/embed/v2/${tt.id}`}
          title={title || "TikTok video"}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          className="w-full max-w-md"
          style={{ minHeight: "740px" }}
        />
      </div>
    );
  }

  if (looksLikeDirectVideo(url)) {
    return (
      <video
        src={url}
        controls
        playsInline
        preload="metadata"
        className="w-full bg-atc-black border-2 border-atc-black"
        data-testid="video-embed-file"
      />
    );
  }

  // Unknown platform — link out
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block border-2 border-atc-black p-5 bg-atc-paper hover:bg-white transition-colors"
      data-testid="video-embed-link"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-atc-muted">Watch the video</div>
      <div className="mt-2 font-heading uppercase text-atc-black break-all text-sm">{url}</div>
    </a>
  );
}
